import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, 
  KeyRound, 
  Shield, 
  Check, 
  Lock, 
  Loader2,
  User,
  Mail,
  Building2,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const MobileSettings = () => {
  const navigate = useNavigate();
  const { 
    operator, 
    deviceToken, 
    trustedUserInfo, 
    setupPin, 
    clearDeviceToken,
    logout
  } = useMobileAuth();
  
  // PIN setup state
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Delete PIN confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Handle PIN input
  const handlePinInput = useCallback((digit) => {
    if (pin.length < 6) {
      setPin(prev => prev + digit);
      setError('');
    }
  }, [pin]);

  const handlePinBackspace = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handleClearPin = useCallback(() => {
    setPin('');
    setError('');
  }, []);

  // PIN setup handler
  const handlePinSetup = async () => {
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await setupPin(pin);
    
    if (result.success) {
      toast.success('PIN set up successfully!');
      setShowPinSetup(false);
      setPin('');
    } else {
      setError(result.error);
      setPin('');
    }
    
    setLoading(false);
  };

  // Delete PIN handler
  const handleDeletePin = () => {
    clearDeviceToken();
    setShowDeleteConfirm(false);
    toast.success('PIN removed. You will need to use email & password next time.');
  };

  // PIN Pad component
  const PinPad = () => (
    <div className="space-y-4">
      {/* PIN Display */}
      <div className="space-y-1.5">
        <Label className="text-gray-400 text-xs uppercase flex items-center gap-1.5">
          <Lock className="h-3 w-3" />
          {deviceToken ? 'Enter New PIN' : 'Create PIN'}
        </Label>
        <div className="flex justify-center gap-2 py-3 bg-[#1a1a1a] rounded-md border border-gray-600">
          {[0,1,2,3,4,5].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? 'bg-[#ffd108]' : 'bg-gray-600'}`}
              data-testid={`settings-pin-dot-${i}`}
            />
          ))}
        </div>
      </div>
      
      {error && (
        <p className="text-red-400 text-sm text-center bg-red-400/10 py-2 rounded-md" data-testid="pin-setup-error">
          {error}
        </p>
      )}
      
      {/* PIN Pad Grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {[1,2,3,4,5,6,7,8,9].map((digit) => (
          <Button
            key={digit}
            variant="outline"
            className="h-12 text-lg font-bold bg-[#1a1a1a] border-gray-600 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
            onClick={() => handlePinInput(String(digit))}
            disabled={loading}
            data-testid={`settings-pin-digit-${digit}`}
          >
            {digit}
          </Button>
        ))}
        <Button
          variant="outline"
          className="h-12 text-xs bg-[#1a1a1a] border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white"
          onClick={handleClearPin}
          disabled={loading}
          data-testid="settings-pin-clear"
        >
          Clear
        </Button>
        <Button
          variant="outline"
          className="h-12 text-lg font-bold bg-[#1a1a1a] border-gray-600 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
          onClick={() => handlePinInput('0')}
          disabled={loading}
          data-testid="settings-pin-digit-0"
        >
          0
        </Button>
        <Button
          variant="outline"
          className="h-12 text-lg bg-[#1a1a1a] border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white"
          onClick={handlePinBackspace}
          disabled={loading}
          data-testid="settings-pin-backspace"
        >
          ←
        </Button>
      </div>
      
      <div className="flex gap-2 mt-2">
        <Button 
          variant="outline"
          onClick={() => {
            setShowPinSetup(false);
            setPin('');
            setError('');
          }}
          className="flex-1 bg-transparent border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white"
          disabled={loading}
          data-testid="cancel-pin-setup"
        >
          Cancel
        </Button>
        <Button 
          onClick={handlePinSetup} 
          className="flex-1 btn-volt"
          disabled={loading || pin.length < 4}
          data-testid="confirm-pin-setup"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (deviceToken ? 'Update PIN' : 'Set PIN')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/mobile/dashboard')} 
            className="text-gray-400 hover:text-white"
            data-testid="settings-back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-white font-bold text-lg">Settings</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Profile Section */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <User className="h-4 w-4 text-[#ffd108]" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-[#1a1a1a] rounded-lg">
              <div className="w-12 h-12 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                <User className="h-6 w-6 text-[#ffd108]" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{operator?.name || 'Operator'}</p>
                <p className="text-gray-400 text-sm flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {operator?.email || 'No email'}
                </p>
                {operator?.client_id && (
                  <p className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                    <Building2 className="h-3 w-3" />
                    {operator.client_id}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-[#ffd108]" />
              Security
            </CardTitle>
            <CardDescription className="text-gray-400 text-sm">
              Manage your login security settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* PIN Status and Actions */}
            {showPinSetup ? (
              <PinPad />
            ) : (
              <>
                {deviceToken ? (
                  // PIN is set
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Check className="h-5 w-5 text-green-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-green-400 font-medium">PIN Enabled</p>
                        <p className="text-gray-400 text-sm">Quick login is active on this device</p>
                        {trustedUserInfo?.trustedAt && (
                          <p className="text-gray-500 text-xs mt-1">
                            Set up: {new Date(trustedUserInfo.trustedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => setShowPinSetup(true)}
                        className="bg-transparent border-gray-600 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
                        data-testid="change-pin-button"
                      >
                        <KeyRound className="h-4 w-4 mr-2" />
                        Change PIN
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="bg-transparent border-red-500/50 text-red-400 hover:bg-red-500/20 hover:text-red-300"
                        data-testid="remove-pin-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove PIN
                      </Button>
                    </div>
                  </div>
                ) : (
                  // No PIN set
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-[#ffd108]/10 rounded-lg border border-[#ffd108]/30">
                      <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                        <KeyRound className="h-5 w-5 text-[#ffd108]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[#ffd108] font-medium">PIN Not Set</p>
                        <p className="text-gray-400 text-sm">Set up a PIN to sign in faster</p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => setShowPinSetup(true)}
                      className="w-full btn-volt"
                      data-testid="setup-pin-button"
                    >
                      <KeyRound className="h-4 w-4 mr-2" />
                      Set Up PIN
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Logout Button */}
        <Button 
          variant="outline"
          onClick={() => {
            logout();
            navigate('/mobile/login');
          }}
          className="w-full bg-transparent border-gray-600 text-gray-400 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/50"
          data-testid="settings-logout-button"
        >
          Sign Out
        </Button>
      </div>

      {/* Delete PIN Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-[#2a2a2a] border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              Remove PIN?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This will remove your PIN from this device. You'll need to sign in with your email and password next time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 bg-transparent border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white"
              data-testid="cancel-delete-pin"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePin}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-pin"
            >
              Remove PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileSettings;
